import { EFinancingProgram } from '@if/core/types'
import { formatDate } from '@if/core/utils'
import {
  ActionButton,
  ActionMenuItem,
  Banner,
  BodyText,
  ControlledNumberField,
  ControlledTextField,
  FieldsetCard,
  SplitContainer,
  useFormContainerContext,
} from '@if/mui'

import { Card, CardContent, debounce, Stack } from '@mui/material'
import { useComputeProductsLoan } from '@src/data/api/calculator-api/calculator-api'
import { isValidFirstPayment } from '@src/data/worksheet-selectors'
import { ETerms, ETermsList, FinancingProgramConfigs, SymTechInsuranceProviderId } from '@src/types/Constants'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { Merchant } from '@src/types/Merchant'
import { ProductsComputeRequestDto } from '@src/types/ProductsComputeRequestDto'
import { ProductsProvider } from '@src/types/ProductsProvider'
import { ProductsWorksheet } from '@src/types/WorksheetSchema'
import { JSX, useEffect, useMemo, useRef, useState } from 'react'
import { useFormState, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import ProductsLoanFundingSummary from './ProductsFundingSummary'
import ProductsInsurances from './ProductsInsurances'
import ProductsSplitApplicationPrompt from './ProductsSplitApplicationPrompt'
import ProductsWorksheetDetails from './ProductsWorksheetDetails'

interface Props {
  merchant: Merchant
  creditApplication: CreditApplication
  productsCapEnabled: boolean
  productsProviders: ProductsProvider[]
  hasContractEndDate: boolean
  onFundingReadyChange: (value: boolean) => void
  castWorksheet: (worksheet: ProductsWorksheet) => ProductsWorksheet
}

const ProductsWorksheetBody = ({
  merchant,
  creditApplication,
  productsCapEnabled,
  productsProviders,
  hasContractEndDate,
  onFundingReadyChange,
  castWorksheet,
}: Props) => {
  const { methods } = useFormContainerContext<ProductsWorksheet>()
  const { control, setValue, getValues, trigger, reset } = methods
  const { errors, isDirty } = useFormState({ control })
  const { t } = useTranslation()

  const [vehicleFinancingTerm, vehicleSellingAmount] = useWatch({
    control,
    name: ['vehicleFinancingTerm', 'vehicleSellingAmount'],
  })
  useEffect(() => {
    void trigger(['creditInsurance', 'extendedWarranty', 'replacementOrGapInsurance'])
  }, [trigger, vehicleFinancingTerm, vehicleSellingAmount])

  const [fundingData, setFundingData] = useState<ProductsComputeRequestDto | undefined>(undefined)
  const [computedFunding, isComputing] = useComputeProductsLoan(fundingData)

  const debouncedSetFundingData = useRef(
    debounce((data: ProductsComputeRequestDto) => setFundingData(data), 1000),
  ).current

  const [creditInsuranceTerm, replacementOrGapInsuranceTerm, extendedWarrantyTerm] = useWatch({
    control,
    name: ['creditInsurance.term', 'replacementOrGapInsurance.term', 'extendedWarranty.term'],
  })

  const maxTermDuration = useMemo(() => {
    const decisionMaxTerm = creditApplication.finalCreditDecision.maxTermDuration ?? 0

    const productTerms = [creditInsuranceTerm, replacementOrGapInsuranceTerm, extendedWarrantyTerm]
    if (productTerms.length === 0) return decisionMaxTerm
    const highestProductTerm = Math.max(...productTerms.filter((term) => term !== null && term !== undefined))
    return Math.min(highestProductTerm, decisionMaxTerm)
  }, [
    creditApplication.finalCreditDecision.maxTermDuration,
    creditInsuranceTerm,
    extendedWarrantyTerm,
    replacementOrGapInsuranceTerm,
  ])

  // SymTech prime: only a SymTech credit insurance is allowed, so the provider is forced
  const isSymTechPrime = creditApplication.isSymTechPrime === true
  const watchedCreditInsuranceProvider = useWatch({ control, name: 'creditInsurance.provider' })
  useEffect(() => {
    if (isSymTechPrime && watchedCreditInsuranceProvider !== SymTechInsuranceProviderId) {
      setValue('creditInsurance.provider', SymTechInsuranceProviderId)
    }
  }, [isSymTechPrime, watchedCreditInsuranceProvider, setValue])

  const [amountRequested, paymentFrequency, term, deliveryOn, firstPaymentOn] = useWatch({
    control,
    name: ['amountRequested', 'paymentFrequency', 'term', 'deliveryOn', 'firstPaymentOn'],
  })

  useEffect(() => {
    if (term != null && term > maxTermDuration) setValue('term', null as unknown as number)
  }, [term, maxTermDuration, setValue])

  const prevAmountRequested = useRef(amountRequested)
  useEffect(() => {
    if (prevAmountRequested.current === amountRequested) return
    prevAmountRequested.current = amountRequested
    if (isDirty) void trigger('amountRequested')
  }, [amountRequested, trigger, isDirty])

  useEffect(() => {
    if (
      [amountRequested, paymentFrequency, term, deliveryOn, firstPaymentOn].every((x) => !!x) &&
      (amountRequested ?? 0) > 0 &&
      isValidFirstPayment(deliveryOn, firstPaymentOn)
    ) {
      const calculationData = {
        isSymTechPrime,
        stateIso: creditApplication.applicant.currentAddress.stateIso,
        amountRequested,
        frequency: paymentFrequency,
        term,
        deliveryOn: formatDate(deliveryOn),
        firstPaymentDate: formatDate(firstPaymentOn),
      } as ProductsComputeRequestDto

      debouncedSetFundingData(calculationData)

      return () => {
        debouncedSetFundingData.clear()
      }
    } else {
      setFundingData(undefined)
    }
  }, [
    creditApplication.applicant.currentAddress.stateIso,
    isSymTechPrime,
    amountRequested,
    paymentFrequency,
    term,
    deliveryOn,
    firstPaymentOn,
    debouncedSetFundingData,
  ])

  // Surface funding readiness: block submission while a compute is in flight or no funding exists yet.
  useEffect(() => {
    onFundingReadyChange(!isComputing && Boolean(computedFunding))
  }, [isComputing, computedFunding, onFundingReadyChange])

  const loanTermChoicesList = useMemo<JSX.Element[]>(
    () =>
      ETermsList.filter(
        (termChoice) => Number(termChoice) >= (ETerms.twelveMonths as number) && Number(termChoice) <= maxTermDuration,
      ).map((item) => <ActionMenuItem key={item} value={item}>{`${item} ${t('common.months')}`}</ActionMenuItem>),
    [maxTermDuration, t],
  )

  const [financedProductsWithTermShorterThanLoanTerm, shouldProposeToSplitApplication] = useMemo(() => {
    const financedProducts: { name: string; term: number }[] = [
      { name: t('productsWorksheet.replacementInsurance'), term: replacementOrGapInsuranceTerm ?? 0 },
      { name: t('productsWorksheet.creditInsurance'), term: creditInsuranceTerm ?? 0 },
      { name: t('productsWorksheet.extendedWarranty'), term: extendedWarrantyTerm ?? 0 },
    ].filter((x) => x.term !== 0)

    const productsShorterThanLoanTerm = financedProducts.filter((x) => term != null && x.term < term)

    const proposeSplit = financedProducts.length > 1 && productsShorterThanLoanTerm.length !== 0

    return [productsShorterThanLoanTerm, proposeSplit]
  }, [t, replacementOrGapInsuranceTerm, creditInsuranceTerm, extendedWarrantyTerm, term])

  const [splitApplicationId, setSplitApplicationId] = useState<string | null>(null)

  return (
    <>
      <SplitContainer
        main={
          <Stack spacing={3}>
            <FieldsetCard title={t('productsWorksheet.vehicle')}>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <ControlledTextField
                    control={control}
                    name="vehicleFinancingTerm"
                    label={`${t('productsWorksheet.vehicleFinancingTerm')} (${t('common.month')})`}
                  />
                  {productsCapEnabled && (
                    <ControlledNumberField
                      control={control}
                      name="vehicleSellingAmount"
                      label={t('productsWorksheet.vehicleSellingAmount')}
                      isMoney
                    />
                  )}
                </Stack>
                <Banner severity="info">
                  <Stack spacing={1}>
                    <BodyText>
                      {t('productsWorksheet.optional', {
                        amount: FinancingProgramConfigs[EFinancingProgram.Products].minLoanAmount,
                      })}
                    </BodyText>
                    <BodyText>
                      {t('productsWorksheet.maxTerm', {
                        maxTermDuration: creditApplication.finalCreditDecision.maxTermDuration,
                      })}
                    </BodyText>
                  </Stack>
                </Banner>
              </Stack>
            </FieldsetCard>

            <ProductsInsurances
              control={control}
              errors={errors}
              setValue={setValue}
              getValues={getValues}
              trigger={trigger}
              stateIso={creditApplication.applicant.currentAddress.stateIso!}
              insurancesProvider={productsProviders.filter((x) => merchant.insuranceProviderIds.includes(x.id))}
              warrantyProvider={productsProviders.filter((x) => merchant.warrantyProviderIds.includes(x.id))}
              hasContractEndDate={hasContractEndDate}
              isSymTechPrime={isSymTechPrime}
            />

            <FieldsetCard title={t('common.financing')}>
              <ProductsWorksheetDetails
                disabledAmountInput
                control={control}
                creditApplication={creditApplication}
                maxTermDuration={maxTermDuration}
                loanTermChoicesList={loanTermChoicesList}
              />
            </FieldsetCard>
          </Stack>
        }
        aside={
          <Stack spacing={2}>
            {shouldProposeToSplitApplication ? (
              <ProductsSplitApplicationPrompt
                financingTerm={term}
                financedProductsWithTermShorterThanLoanTerm={financedProductsWithTermShorterThanLoanTerm}
                creditApplicationId={creditApplication.id}
                unvalidatedWorksheet={getValues()}
                onNewApplicationCreated={(newAppId, updatedWorksheetData) => {
                  setSplitApplicationId(newAppId)
                  reset(castWorksheet(updatedWorksheetData), { keepDefaultValues: false })
                  setValue('versionTag', creditApplication.worksheet?.versionTag ?? '')
                }}
              />
            ) : (
              <ProductsLoanFundingSummary computedFunding={computedFunding} isFetching={isComputing} />
            )}

            {splitApplicationId !== null && (
              <Card>
                <CardContent>
                  <BodyText sx={{ pb: 2 }}>
                    {t('productsWorksheet.splitApplicationPrompt.productsHaveBeenMovedToANewApplication')}
                  </BodyText>
                  <ActionButton
                    emphasis="principal"
                    onClick={() =>
                      window.open(`/credits/products/${splitApplicationId}/worksheet`, '_blank', 'noopener,noreferrer')
                    }
                  >
                    {t('productsWorksheet.splitApplicationPrompt.viewNewApplication')}
                  </ActionButton>
                </CardContent>
              </Card>
            )}
          </Stack>
        }
      />
    </>
  )
}

export default ProductsWorksheetBody
